import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { holdsFigureSlots } from "@/zapi-frontend/shared/lib/figureRefs";
import { useModelLedger } from "../../../ModelLedgerIsland/useModelLedger";
import { FigureRefsProvider } from "../../../MemoIsland/FigureRefs/FigureRefsProvider";
import { ProjectHero } from "./ProjectHero";
import { OpportunityFrameBlock } from "./OpportunityFrameBlock";
import { SuccessCriteriaBlock } from "./SuccessCriteriaBlock";
import { KeyInsightsBlock } from "./KeyInsightsBlock";
import { NextStepsBlock } from "./NextStepsBlock";

export function DecisionBriefOverview() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { selectedProject, syncOverviewIfNeeded, syncingOverviewProjectId } = useProjectStore();
  const ledger = useModelLedger(selectedProject ? String(selectedProject.id) : "");
  if (!selectedProject) return null;

  const overview = selectedProject.overview;
  const nextSteps = selectedProject.nextSteps || [];
  const isSyncingOverview = syncingOverviewProjectId === selectedProject.id;
  const waitsForLedger =
    ledger.isLoading && (holdsFigureSlots([overview, nextSteps]) || !!selectedProject.decisionStatusRefs?.length);
  // A runner project's Home page only changes through Leo; Refresh is a snippets-project button.
  const isRunnerProject = selectedProject.projectTemplateSlug === "";

  const handleRefresh = async () => {
    if (isSyncingOverview) return;
    try {
      await syncOverviewIfNeeded(selectedProject.id);
    } catch {
      toast.error(t("overview.failedToRefresh"));
    }
  };

  if (waitsForLedger) {
    return <PageLoader data-testid="overview-figures-loading" />;
  }

  return (
    <FigureRefsProvider figures={ledger.data?.figures}>
      <div className="w-full">
        <div className="flex flex-col gap-6 pb-8">
          <ProjectHero
            project={selectedProject}
            onRefresh={isRunnerProject ? undefined : handleRefresh}
            isRefreshing={isSyncingOverview}
          />
          {overview ? (
            <div className="flex flex-col gap-4">
              <OpportunityFrameBlock
                overview={overview}
                decisionStatus={selectedProject.decisionStatus}
                decisionStatusRefs={selectedProject.decisionStatusRefs}
              />
              <SuccessCriteriaBlock overview={overview} />
              <NextStepsBlock nextSteps={nextSteps} />
              <KeyInsightsBlock overview={overview} />
            </div>
          ) : (
            <NextStepsBlock nextSteps={nextSteps} />
          )}
        </div>
      </div>
    </FigureRefsProvider>
  );
}
